// Shared game-screen control for leaving a game and returning to the library.

import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { useRoomConfig, useRoomGameExit, useRoomPlayers } from '../../hooks';
import { analyticsService } from '../../services/analyticsService';

const ExitGameControl: React.FC = () => {
  const { exitToLibrary, isClosing } = useRoomGameExit({
    linkLocation: 'exit_game_modal',
  });
  const { roomCode, gameName } = useRoomConfig();
  const { playerCount } = useRoomPlayers();

  const handleExitGame = () => {
    void exitToLibrary();
  };

  const trackCancel = () => {
    analyticsService.trackButtonClick('Cancel', {
      link_location: 'exit_game_modal',
      room_code: roomCode || 'unknown',
      game_name: gameName || 'Unknown Game',
      player_count: playerCount,
    });
  };

  return (
    <AlertDialog>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ delay: 0.4 }}
        className="fixed left-10 top-10 z-[410]"
      >
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            aria-label="Exit game"
            className="size-20 rounded-full border border-neutral-50/50 !bg-neutral-50/25 p-0 text-neutral-50 shadow-sm backdrop-blur-xl hover:!bg-neutral-50/25 hover:text-primary"
          >
            <X aria-hidden="true" className="size-[64px]" strokeWidth={2.75} />
          </Button>
        </AlertDialogTrigger>
      </motion.div>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Exit game?</AlertDialogTitle>
          <AlertDialogDescription>
            Leave this game and return to the game library?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            type="button"
            disabled={isClosing}
            onClick={trackCancel}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction disabled={isClosing} onClick={handleExitGame}>
            Go to game library
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ExitGameControl;
